import React, { createContext, useContext, useState, useEffect } from 'react';
import { format } from 'date-fns';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [data, setData] = useState({
    waterTests: [],
    feedingLogs: [],
    growthData: [],
    timeline: [],
    maintenanceLogs: [],
    imageUploads: [],
    systemInfo: {
      tankSize: 1000,
      stockingDate: null,
      initialFishCount: 0,
      targetHarvestDate: null,
      targetWeight: 300,
      location: ''
    },
    tanks: [
      { id: 1, name: 'Tank 1', size: 1000, isActive: true },
      { id: 2, name: 'Tank 2', size: 500, isActive: false },
      { id: 3, name: 'Tank 3', size: 2000, isActive: false },
      { id: 4, name: 'Tank 4', size: 5000, isActive: false }
    ],
    activeTankId: 1
  });

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('tilapiaData');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setData(prevData => ({
          ...prevData,
          ...parsedData
        }));
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
      }
    }
  }, []);

  // Save data to localStorage whenever data changes
  useEffect(() => {
    localStorage.setItem('tilapiaData', JSON.stringify(data));
  }, [data]);

  const getCurrentTimestamp = () => {
    return new Date().toISOString();
  };

  const addWaterTest = (waterTest) => {
    const newTest = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd'),
      timestamp: getCurrentTimestamp(),
      tankId: data.activeTankId,
      ...waterTest
    };
    setData(prev => ({
      ...prev,
      waterTests: [...prev.waterTests, newTest]
    }));
  };

  const addFeedingLog = (feedingLog) => {
    const newLog = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd'),
      timestamp: getCurrentTimestamp(),
      tankId: data.activeTankId,
      ...feedingLog
    };
    setData(prev => ({
      ...prev,
      feedingLogs: [...prev.feedingLogs, newLog]
    }));
  };

  const addGrowthData = (growthData) => {
    const newData = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd'),
      timestamp: getCurrentTimestamp(),
      tankId: data.activeTankId,
      ...growthData
    };
    setData(prev => ({
      ...prev,
      growthData: [...prev.growthData, newData]
    }));
  };

  const addTimelineTask = (task) => {
    const newTask = {
      id: Date.now(),
      timestamp: getCurrentTimestamp(),
      tankId: data.activeTankId,
      ...task
    };
    setData(prev => ({
      ...prev,
      timeline: [...prev.timeline, newTask]
    }));
  };

  const updateTimelineTask = (taskId, updates) => {
    setData(prev => ({
      ...prev,
      timeline: prev.timeline.map(task => 
        task.id === taskId ? { ...task, ...updates } : task
      )
    }));
  };

  const addMaintenanceLog = (maintenanceLog) => {
    const newLog = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd'),
      timestamp: getCurrentTimestamp(),
      tankId: data.activeTankId,
      ...maintenanceLog
    };
    setData(prev => ({
      ...prev,
      maintenanceLogs: [...prev.maintenanceLogs, newLog]
    }));
  };

  const addImageUpload = (imageUpload) => {
    const newUpload = {
      id: Date.now(),
      date: format(new Date(), 'yyyy-MM-dd'),
      timestamp: getCurrentTimestamp(),
      tankId: data.activeTankId,
      ...imageUpload
    };
    setData(prev => ({
      ...prev,
      imageUploads: [...prev.imageUploads, newUpload]
    }));
  };

  const updateSystemInfo = (updates) => {
    setData(prev => ({
      ...prev,
      systemInfo: { ...prev.systemInfo, ...updates }
    }));
  };

  const deleteEntry = (type, id) => {
    setData(prev => ({
      ...prev,
      [type]: prev[type].filter(item => item.id !== id)
    }));
  };

  const setActiveTask = (tankId) => {
    setData(prev => ({
      ...prev,
      activeTankId: tankId,
      tanks: prev.tanks.map(tank => ({
        ...tank,
        isActive: tank.id === tankId
      }))
    }));
  };

  const getActiveTank = () => {
    return data.tanks.find(tank => tank.isActive) || data.tanks[0];
  };

  const getLatestWaterTest = () => {
    const activeTankId = data.activeTankId;
    const tankTests = data.waterTests.filter(test => test.tankId === activeTankId);
    return tankTests.length > 0 ? tankTests[tankTests.length - 1] : null;
  };

  const getLatestGrowthData = () => {
    const activeTankId = data.activeTankId;
    const tankGrowthData = data.growthData.filter(item => item.tankId === activeTankId);
    return tankGrowthData.length > 0 ? tankGrowthData[tankGrowthData.length - 1] : null;
  };

  const getUpcomingTasks = () => {
    const activeTankId = data.activeTankId;
    const today = new Date();
    return data.timeline
      .filter(task => 
        task.status === 'pending' && 
        new Date(task.dueDate) >= today &&
        (task.tankId === activeTankId || !task.tankId) // Include global tasks
      )
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  };

  const calculateFCR = () => {
    const activeTankId = data.activeTankId;
    const tankFeedingLogs = data.feedingLogs.filter(log => log.tankId === activeTankId);
    const tankGrowthData = data.growthData.filter(item => item.tankId === activeTankId);
    
    if (tankFeedingLogs.length === 0 || tankGrowthData.length < 2) return 0;
    
    const totalFeed = tankFeedingLogs.reduce((sum, log) => sum + log.totalFeed, 0);
    const firstGrowth = tankGrowthData[0];
    const lastGrowth = tankGrowthData[tankGrowthData.length - 1];
    
    if (!firstGrowth || !lastGrowth) return 0;
    
    const weightGain = (lastGrowth.averageWeight - firstGrowth.averageWeight) * lastGrowth.totalFish / 1000; // kg
    return weightGain > 0 ? (totalFeed / 1000) / weightGain : 0;
  };

  const value = {
    data,
    addWaterTest,
    addFeedingLog,
    addGrowthData,
    addTimelineTask,
    updateTimelineTask,
    addMaintenanceLog,
    addImageUpload,
    updateSystemInfo,
    deleteEntry,
    getLatestWaterTest,
    getLatestGrowthData,
    getUpcomingTasks,
    calculateFCR,
    setActiveTask,
    getActiveTank
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};